import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../store/store";
import {
  getUserNotifications,
  getAllNotifications,
  markNotificationAsRead,
  markGlobalNotificationAsRead,
  markAllNotificationsAsRead,
  selectUserNotifications,
  selectAllNotifications,
  selectNotificationsLoading,
  selectNewNotification,
  createNotification,
  subscribeToUserNotifications,
  setNewNotification,
} from "../../store/slices/notificationSlice";
import Layout from "../../components/layout/layout";
import NotificationCard from "./NotificationCard";

import { getAuth } from "firebase/auth";
import { getFirestore, doc, getDoc, updateDoc, onSnapshot } from "firebase/firestore";
import { ref, update, get, getDatabase, onValue, off } from "firebase/database"; // Import RTDB functions
import { app } from "../../config/firebase"; // Import the rtdb reference
const rtdb = getDatabase(app);

import { useAppState } from "../../hooks/useAppState";
import { Wallet, Bell, Video, Package } from "lucide-react";
import { transferFunds } from "../../services/walletService";
// Hook to auto-mark fund request notifications as read if status is not pending
function useAutoMarkFundRequestRead(notification: any, userId: string | undefined, dispatch: any) {
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    if (
      (notification.trigger === "fund_request_receive" ||
        notification.trigger === "fund_request" ||
        notification.trigger === "fund_request_details") &&
      notification.meta?.requestId &&
      userId &&
      !notification.read
    ) {
      const db = getFirestore();
      const fundRequestRef = doc(db, "fundRequests", notification.meta.requestId);

      unsubscribe = onSnapshot(
        fundRequestRef,
        (fundRequestDoc) => {
          if (fundRequestDoc.exists()) {
            const data = fundRequestDoc.data();
            if (data.status !== "pending" && !notification.read) {
              dispatch(
                markNotificationAsRead({
                  userId,
                  notificationId: notification.id,
                })
              );
            }
          } else {
            // If doc doesn't exist, also mark as read
            if (!notification.read) {
              dispatch(
                markNotificationAsRead({
                  userId,
                  notificationId: notification.id,
                })
              );
            }
          }
        },
        () => {
          // On error, do nothing
        }
      );
    }

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [notification, userId, dispatch]);
}

// Hook to auto-mark "new_support_request" notifications as read when request status is not "requested"
function useAutoMarkSupportRequestRead(notification: any, userId: string | undefined, dispatch: any) {
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    if (
      notification.trigger === "new_support_request" &&
      notification.meta?.requestId &&
      userId &&
      !notification.read
    ) {
      const requestId = notification.meta.requestId;
      const requestRef = ref(rtdb, `requests/${userId}/${requestId}`);
      // Use onValue for RTDB
      const callback = (snapshot: any) => {
        const data = snapshot.val();
        if (data && data.status && data.status !== "requested" && !notification.read) {
          dispatch(
            markNotificationAsRead({
              userId,
              notificationId: notification.id,
            })
          );
        }
      };
      onValue(requestRef, callback);
      unsubscribe = () => {
        off(requestRef, "value", callback);
      };
    }

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [notification, userId, dispatch]);
}

// Wrapper component to use the hook safely
function NotificationWithAutoRead({
  notification,
  onMarkAsRead,
  onSupportRequestAction,
  onFundRequestAction,
  user,
  userId,
  dispatch,
}: any) {
  useAutoMarkFundRequestRead(notification, userId, dispatch);
  useAutoMarkSupportRequestRead(notification, userId, dispatch);
  return (
    <NotificationCard
      notification={notification}
      onMarkAsRead={onMarkAsRead}
      onSupportRequestAction={onSupportRequestAction}
      onFundRequestAction={onFundRequestAction}
      user={user}
    />
  );
}

export function NotificationComponents() {
  const dispatch = useDispatch<AppDispatch>();
  const auth = getAuth();
  const { user } = useAppState();
  const userId = auth.currentUser?.uid;

  const userNotifications = useSelector(selectUserNotifications);
  const allNotifications = useSelector(selectAllNotifications);
  const isLoading = useSelector(selectNotificationsLoading);
  const newNotification = useSelector(selectNewNotification);

  // State for notification filtering
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const [filteredNotifications, setFilteredNotifications] = useState<any[]>([]);
  // Only display user notifications in this component
  // Admin notifications are handled in AdminNotification component
  const displayedNotifications = userNotifications;

  // Filter notifications based on the selected type
  useEffect(() => {
    if (activeFilter === "all") {
      setFilteredNotifications(displayedNotifications);
    } else if (activeFilter === "fund_requests") {
      setFilteredNotifications(
        displayedNotifications.filter(
          (notification) =>
            notification.trigger === "fund_request" ||
            notification.trigger === "fund_request_receive" ||
            notification.trigger === "fund_request_send" ||
            notification.trigger === "fund_request_approved" ||
            notification.trigger === "fund_request_rejected" ||
            notification.trigger === "funds_distributed" ||
            notification.trigger === "funds_received" ||
            notification.trigger === "funds_added"
        )
      );
    } else if (activeFilter === "unread") {
      setFilteredNotifications(
        displayedNotifications.filter((notification) => !notification.read)
      );
    } else if (activeFilter === "support_requests") {
      setFilteredNotifications(
        displayedNotifications.filter(
          (notification) =>
            notification.trigger === "new_support_request" ||
            notification.trigger === "support_request_submitted" ||
            notification.trigger === "support_request_accepted" ||
            notification.trigger === "support_request_rejected" ||
            notification.trigger === "support_request_missed"
        )
      );
    } else if (activeFilter === "package_purchases") {
      setFilteredNotifications(
        displayedNotifications.filter(
          (notification) =>
            notification.trigger === "package_purchased" ||
            notification.trigger === "package_purchase_success"
        )
      );
    } else {
      setFilteredNotifications(
        displayedNotifications.filter(
          (notification) =>
            notification.trigger !== "fund_request" &&
            notification.trigger !== "fund_request_receive" &&
            notification.trigger !== "fund_request_send" &&
            notification.trigger !== "fund_request_approved" &&
            notification.trigger !== "fund_request_rejected" &&
            notification.trigger !== "funds_received" &&
            notification.trigger !== "funds_distributed" &&
            notification.trigger !== "funds_added" &&
            notification.trigger !== "new_support_request" &&
            notification.trigger !== "support_request_submitted" &&
            notification.trigger !== "support_request_accepted" &&
            notification.trigger !== "support_request_rejected" &&
            notification.trigger !== "support_request_missed" &&
            notification.trigger !== "package_purchased"
        )
      );
    }
  }, [activeFilter, displayedNotifications]);
  useEffect(() => {
    const setupNotifications = async () => {
      if (userId) {
        // Set up real-time notification subscription
        await dispatch(subscribeToUserNotifications(userId));

        // Initial fetch of notifications
        await dispatch(getUserNotifications(userId));
      }
    };

    setupNotifications();

    // When the component unmounts, clear the new notification state
    return () => {
      dispatch(setNewNotification(null));
    };
  }, [dispatch, userId]);
  const handleMarkAllAsRead = async () => {
    if (userId) {
      // Use the optimized batch function to mark all notifications as read
      await dispatch(markAllNotificationsAsRead(userId));
    }
  };
  const handleMarkAsRead = async (notificationId: string) => {
    if (userId && notificationId) {
      // For regular users, mark their own notifications as read
      await dispatch(markNotificationAsRead({ userId, notificationId }));
    }
  }; // Handle fund request management (approve/reject)
  const handleFundRequestAction = async (notification: any, action: string) => {
    try {
      // Get the request details from the notification
      const { meta } = notification;
      const requestId = meta?.requestId;
      const amount = meta?.amount;
      const requesterUid = meta?.requesterUid;
      const requesterEmail = meta?.requesterEmail;

      if (!requestId) {
        console.error("Missing requestId in fund request notification");
        return;
      }

      // Get Firestore reference
      const db = getFirestore();
      const fundRequestRef = doc(db, "fundRequests", requestId);

      // Get current fund request data
      const fundRequestDoc = await getDoc(fundRequestRef);
      if (!fundRequestDoc.exists()) {
        console.error(`Fund request with ID ${requestId} not found`);
        return;
      }

      const fundRequestData = fundRequestDoc.data();

      if (action === "accept" || action === "approve") {
        // 1. Check org wallet balance before proceeding
        try {
          const fromWalletId = userId; // Organization wallet (current user is org owner)
          const toWalletId = fundRequestData.requesterId || requesterUid;
          const transferAmount = fundRequestData.amount || amount;

          if (!fromWalletId || !toWalletId || !transferAmount) {
            throw new Error("Missing required data for fund transfer");
          }

          // Fetch org wallet balance from Firestore (assuming wallet is a collection)
          const walletDocRef = doc(db, "wallets", fromWalletId);
          const walletDocSnap = await getDoc(walletDocRef);
          const walletData = walletDocSnap.exists() ? walletDocSnap.data() : null;
          const orgBalance = walletData?.balance ?? 0;

          if (Number(orgBalance) < Number(transferAmount)) {
            window.alert("Your organization doesn't have enough funds to approve this request. Please add funds to your organization wallet first.");
            // Do NOT mark as read if insufficient balance
            return;
          }

          // Mark the notification as read first (only if balance is valid)
          if (notification.id && userId) {
            dispatch(
              markNotificationAsRead({
                userId,
                notificationId: notification.id,
              })
            );
          }

          // Transfer funds from org wallet to requester's wallet
          const transferResult = await transferFunds(
            fromWalletId,
            toWalletId,
            transferAmount,
            userId
          );

          if (!transferResult.success) {
            // Check for specific error types and provide friendly messages
            if (transferResult.error?.includes("Insufficient balance")) {
              window.alert(
                `Your organization doesn't have enough funds to approve this request. Please add funds to your organization wallet first.`
              );
              // Do NOT mark as read if transfer fails due to balance
              return;
            } else {
              window.alert(
                `Failed to transfer funds: ${transferResult.error}`
              );
              // Do NOT mark as read if transfer fails
              return;
            }
          }

          // Update the fund request status in Firestore
          await updateDoc(fundRequestRef, {
            status: "approved",
            updatedAt: new Date(),
            isRead: true,
            approvedAt: new Date(),
            approvedBy: userId,
            approverName: user?.name || "Organization Owner",
          });

          // Send notification to the requester
          if (requesterUid) {
            dispatch(
              createNotification({
                userId: requesterUid,
                userEmail: requesterEmail || fundRequestData.requesterEmail,
                message: `Your fund request of $${
                  transferAmount.toFixed(2) || "0.00"
                } has been approved`,
                trigger: "fund_request_approved",
                triggeredBy: userId || "unknown",
                meta: {
                  ...meta,
                  status: "approved",
                  approvedAt: new Date().toISOString(),
                  approvedBy: userId,
                  customerEmail: user?.email || "Unknown",
                  approverName: user?.name || "Organization Owner",
                },
              })
            );
            // Also notify the approver (organization owner)
            dispatch(
              createNotification({
                userId: userId,
                userEmail: user?.email || "",
                message: `You approved a fund request of $${transferAmount.toFixed(2) || "0.00"} for ${fundRequestData.requesterEmail || requesterEmail}`,
                trigger: "fund_request_approved",
                triggeredBy: userId || "unknown",
                meta: {
                  ...meta,
                  status: "approved",
                  approvedAt: new Date().toISOString(),
                  approvedBy: userId,
                  requesterEmail: fundRequestData.requesterEmail || requesterEmail,
                  requesterName: fundRequestData.requesterName || "",
                },
              })
            );
          }
        } catch (error) {
          console.error("Error approving fund request:", error);
          // Do NOT mark as read if error
          return;
        }
      } else if (action === "reject") {
        // Mark as read for reject action (no balance check needed)
        if (notification.id && userId) {
          dispatch(
            markNotificationAsRead({
              userId,
              notificationId: notification.id,
            })
          );
        }
        // ...existing code for reject...
        try {
          await updateDoc(fundRequestRef, {
            status: "rejected",
            updatedAt: new Date(),
            isRead: true,
            rejectedAt: new Date(),
            rejectedBy: userId,
            rejectorName: user?.name || "Organization Owner",
          });

          // Send notification to the requester
          if (requesterUid || fundRequestData.requesterId) {
            const recipientId = requesterUid || fundRequestData.requesterId;
            const requestAmount = fundRequestData.amount || amount;

            dispatch(
              createNotification({
                userId: recipientId,
                userEmail: requesterEmail || fundRequestData.requesterEmail,
                message: `Your fund request of $${
                  requestAmount.toFixed(2) || "0.00"
                } has been rejected`,
                trigger: "fund_request_rejected",
                triggeredBy: userId || "unknown",
                meta: {
                  ...meta,
                  requestId: requestId,
                  status: "rejected",
                  rejectedAt: new Date().toISOString(),
                  rejectedBy: userId,
                  rejectorName: user?.name || "Organization Owner",
                },
              })
            );
            // Also notify the approver (organization owner)
            dispatch(
              createNotification({
                userId: userId,
                userEmail: user?.email || "",
                message: `You rejected a fund request of $${requestAmount.toFixed(2) || "0.00"} for ${fundRequestData.requesterEmail || requesterEmail}`,
                trigger: "fund_request_rejected",
                triggeredBy: userId || "unknown",
                meta: {
                  ...meta,
                  requestId: requestId,
                  status: "rejected",
                  rejectedAt: new Date().toISOString(),
                  rejectedBy: userId,
                  requesterEmail: fundRequestData.requesterEmail || requesterEmail,
                  requesterName: fundRequestData.requesterName || "",
                },
              })
            );
          }
        } catch (error) {
          console.error("Error rejecting fund request:", error);
          // Do NOT mark as read if error
          return;
        }
      }
    } catch (error) {
      console.error(`Error handling fund request ${action}:`, error);
      // Do NOT mark as read if error
    }
  }; // Handle support request actions (accept/reject)
  const handleSupportRequestAction = async (
    notification: any,
    action: string
  ) => {
    try {
      const { meta } = notification;
      const requestId = meta?.requestId;
      const customerId = meta?.customerId;
      const customerEmail = meta?.customerEmail;
      const question = meta?.question || "";

      if (!requestId || !customerId) {
        console.error("Missing required data for support request action");
        return;
      }

      // Update the request status in RTDB
      const updateRequestStatus = async (
        status: string,
        rejectReason?: string
      ) => {
        try {
          // Direct update to RTDB
          if (userId && requestId) {
            console.log(`Updating request ${requestId} status to ${status}`);

            // Get the correct path for the request in RTDB
            // For new requests, the request is under the expert's ID
            const requestRef = ref(rtdb, `requests/${userId}/${requestId}`);

            // First check if the request exists
            const snapshot = await get(requestRef);
            if (!snapshot.exists()) {
              console.error(`Request ${requestId} not found in database`);
              return null;
            }

            // Prepare update data
            const updateData: any = { status };

            if (status === "accepted") {
              updateData.roomId = `room_${Date.now()}_${userId}_${customerId}`;
            } else if (status === "rejected" && rejectReason) {
              updateData.reject_reason = rejectReason;
            } else if (status === "missed") {
              updateData.missed_at = new Date().toISOString();
              updateData.missed_reason =
                "Expert did not respond within the time limit";
            }

            // Update the request status
            await update(requestRef, updateData);
            console.log(
              `Request ${requestId} status updated to ${status}`,
              updateData
            );

            return updateData.roomId; // Return roomId if created
          }
        } catch (error) {
          console.error("Error updating request status:", error);
        }
        return null;
      };

      // Get request details from database
      const getRequestDetails = async (expertId: string, requestId: string) => {
        try {
          const requestRef = ref(rtdb, `requests/${expertId}/${requestId}`);
          const snapshot = await get(requestRef);

          if (snapshot.exists()) {
            return snapshot.val();
          } else {
            console.error(`Request ${requestId} not found in database`);
            return null;
          }
        } catch (error) {
          console.error("Error fetching request details:", error);
          return null;
        }
      };

      if (action === "accept") {
        // Generate a room ID for the video call
        const roomId = `room_${Date.now()}_${userId}_${customerId}`; // Get request details from RTDB
        const requestDetails = await getRequestDetails(userId || "", requestId);
        console.log("Fetched request details:", requestDetails);

        // Only proceed if the request exists and is in 'requested' status
        if (!requestDetails) {
          console.error(
            `Cannot accept request ${requestId}: Request not found in database`
          );
          return;
        }

        if (requestDetails.status !== "requested") {
          console.error(
            `Cannot accept request ${requestId}: Request status is ${requestDetails.status}`
          );
          return;
        }

        // Update request status
        const updatedRoomId = await updateRequestStatus("accepted");
        const finalRoomId = updatedRoomId || roomId;

        // Mark notification as read only after successful status update
        if (notification.id && userId) {
          dispatch(
            markNotificationAsRead({
              userId,
              notificationId: notification.id,
            })
          );
        }

        // Construct RTC URL for expert
        const rtcUrl = `${
          import.meta.env.VITE_SUPPORTY_RTC
        }/id=${finalRoomId}/name=${user?.name || "Expert"}/email=${
          user?.email || ""
        }/expertId=${userId}/customerId=${customerId}/role=expert`;

        // Send notification to the customer with auto-join flag
        dispatch(
          createNotification({
            userId: customerId,
            userEmail: customerEmail || "",
            message: `Your support request has been accepted by ${
              user?.name || "Support Expert"
            }. Joining the session now.`,
            trigger: "support_request_accepted",
            triggeredBy: userId || "unknown",
            meta: {
              ...meta,
              requestId: requestId,
              customerId: customerId,
              customerName: meta?.customerName || requestDetails?.user?.name,
              customerEmail: customerEmail || requestDetails?.user?.email,
              expertName: user?.name || "Support Expert",
              expertEmail: user?.email || "",
              status: "accepted",
              acceptedAt: new Date().toISOString(),
              acceptedBy: userId,
              roomId: finalRoomId,
              question: question || requestDetails?.question,

              handled: true, // Mark as handled to prevent auto-missed
            },
          })
        );
        // Send notification to the expert (current user) with auto-join flag
        dispatch(
          createNotification({
            userId: userId,
            userEmail: user?.email || "",
            message: `You accepted a support request from ${meta?.customerName || requestDetails?.user?.name}. Joining the session now.`,
            trigger: "support_request_accepted",
            triggeredBy: userId || "unknown",
            meta: {
              ...meta,
              requestId: requestId,
              customerId: customerId,
              customerName: meta?.customerName || requestDetails?.user?.name,
              customerEmail: customerEmail || requestDetails?.user?.email,
              expertName: user?.name || "Support Expert",
              expertEmail: user?.email || "",
              status: "accepted",
              acceptedAt: new Date().toISOString(),
              acceptedBy: userId,
              roomId: finalRoomId,
              question: question || requestDetails?.question,
              handled: true,
            },
          })
        );

        // Open the video call window for the expert immediately
        window.open(rtcUrl, "_blank");
      } else if (action === "reject") {
        const rejectReason = "Expert is unavailable at the moment.";
        const requestDetails = await getRequestDetails(userId || "", requestId);
        console.log("Fetched request details for rejection:", requestDetails);

        // Only proceed if the request exists and is in 'requested' status
        if (!requestDetails) {
          console.error(
            `Cannot reject request ${requestId}: Request not found in database`
          );
          return;
        }

        if (requestDetails.status !== "requested") {
          console.error(
            `Cannot reject request ${requestId}: Request status is ${requestDetails.status}`
          );
          return;
        }

        // Update request status
        await updateRequestStatus("rejected", rejectReason);

        // Mark notification as read only after successful status update
        if (notification.id && userId) {
          dispatch(
            markNotificationAsRead({
              userId,
              notificationId: notification.id,
            })
          );
        }

        // Send notification to the customer - without autoJoin flag
        dispatch(
          createNotification({
            userId: customerId,
            userEmail: customerEmail || requestDetails?.user?.email || "",
            message: `Your support request has been rejected by ${
              user?.name || "Support Expert"
            }. Reason: ${rejectReason}`,
            trigger: "support_request_rejected",
            triggeredBy: userId || "unknown",
            meta: {
              ...meta,
              requestId: requestId,
              customerId: customerId,
              customerName: meta?.customerName || requestDetails?.user?.name,
              customerEmail: customerEmail || requestDetails?.user?.email,
              expertName: user?.name || "Support Expert",
              expertEmail: user?.email || "",
              status: "rejected",
              rejectedAt: new Date().toISOString(),
              rejectedBy: userId,
              rejectReason: rejectReason,
              question: question || requestDetails?.question,
              handled: true, // Mark as handled to prevent auto-missed
            },
          })
        );
        // Send notification to the expert (current user)
        dispatch(
          createNotification({
            userId: userId,
            userEmail: user?.email || "",
            message: `You rejected a support request from ${meta?.customerName || requestDetails?.user?.name}. Reason: ${rejectReason}`,
            trigger: "support_request_rejected",
            triggeredBy: userId || "unknown",
            meta: {
              ...meta,
              requestId: requestId,
              customerId: customerId,
              customerName: meta?.customerName || requestDetails?.user?.name,
              customerEmail: customerEmail || requestDetails?.user?.email,
              expertName: user?.name || "Support Expert",
              expertEmail: user?.email || "",
              status: "rejected",
              rejectedAt: new Date().toISOString(),
              rejectedBy: userId,
              rejectReason: rejectReason,
              question: question || requestDetails?.question,
              handled: true,
            },
          })
        );
      } 
    } catch (error) {
      console.error(`Error handling support request ${action}:`, error);
    }
  };
  return (
    <Layout>
      {/* Toast notification for new notifications */}{" "}
      <div className="mx-auto  rounded-lg"  style={{
          height: "calc(100vh - 112px)",
          overflowY: "auto",
            overflowX:'hidden'
        }} >
        <div className="space-y-6 ">
          {/* Settings */}
          <div className="bg-white rounded-lg shadow p-6">
            {" "}
            <div className="flex items-center justify-between p-2 gap-3">
              <div>
                <h2 className="text-[20px] text-[#696969] nunito-semibold ">
                  Notifications
                </h2>
              </div>
              <div>
                <button
                  className="border-[#F15D18] border-[1px] w-[200px] h-[48px] rounded-[8px] text-[16px] text-[#F15D18] nunito-bold"
                  onClick={handleMarkAllAsRead}
                >
                  Mark all as Read
                </button>
              </div>
            </div>{" "}
            {/* Filter Buttons */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setActiveFilter("all")}
                className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-all ${
                  activeFilter === "all"
                    ? "bg-[#F15D18] text-white"
                    : "bg-[#FEE4D0] text-[#F15D18]"
                }`}
              >
                All
              </button>
              <button
                onClick={() => setActiveFilter("support_requests")}
                className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-all flex items-center justify-center ${
                  activeFilter === "support_requests"
                    ? "bg-[#F15D18] text-white"
                    : "bg-[#FEE4D0] text-[#F15D18]"
                }`}
              >
                <Video className="w-4 h-4 mr-1" />
                Supporty (
                {
                  displayedNotifications.filter(
                    (n) =>
                      n.trigger === "new_support_request" ||
                      n.trigger === "support_request_submitted" ||
                      n.trigger === "support_request_accepted" ||
                      n.trigger === "support_request_rejected" ||
                      n.trigger === "support_request_missed"
                  ).length
                }
                )
              </button>
              {user?.role === "Customer" && (

             
              <button
                onClick={() => setActiveFilter("fund_requests")}
                className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-all flex items-center justify-center ${
                  activeFilter === "fund_requests"
                    ? "bg-[#F15D18] text-white"
                    : "bg-[#FEE4D0] text-[#F15D18]"
                }`}
              >
                <Wallet className="w-4 h-4 mr-1" />
                Funds (
                {
                  displayedNotifications.filter(
                    (n) =>
                      n.trigger === "fund_request" ||
                      n.trigger === "fund_request_receive" ||
                      n.trigger === "fund_request_approved" ||
                      n.trigger === "fund_request_rejected" ||
                      n.trigger === "funds_distributed" ||
                      n.trigger === "funds_received" ||
                      n.trigger === "funds_added"
                  ).length
                }
                )
              </button> )}
              <button
                onClick={() => setActiveFilter("package_purchases")}
                className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-all flex items-center justify-center ${
                  activeFilter === "package_purchases"
                    ? "bg-[#F15D18] text-white"
                    : "bg-[#FEE4D0] text-[#F15D18]"
                }`}
              >
                <Package className="w-4 h-4 mr-1" />
                Packages (
                {
                  displayedNotifications.filter(
                    (n) =>
                      n.trigger === "package_purchased" 
                  ).length
                }
                )
              </button>
              <button
                onClick={() => setActiveFilter("unread")}
                className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-all ${
                  activeFilter === "unread"
                    ? "bg-[#F15D18] text-white"
                    : "bg-[#FEE4D0] text-[#F15D18]"
                }`}
              >
                Unread
              </button>
              <button
                onClick={() => setActiveFilter("other")}
                className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-all ${
                  activeFilter === "other"
                    ? "bg-[#F15D18] text-white"
                    : "bg-[#FEE4D0] text-[#F15D18]"
                }`}
              >
                Other
              </button>
            </div>{" "}
            {/* Notification List */}
            <div className="mt-1 space-y-4 px-2">
              {" "}
              {isLoading ? (
                <div className="text-center py-4">Loading notifications...</div>
              ) : filteredNotifications.length > 0 ? (
                filteredNotifications.map((notification) => (
                  <div key={notification.id} className="mb-4">
                    <NotificationWithAutoRead
                      notification={notification}
                      onMarkAsRead={handleMarkAsRead}
                      onSupportRequestAction={handleSupportRequestAction}
                      onFundRequestAction={handleFundRequestAction}
                      user={user}
                      userId={userId}
                      dispatch={dispatch}
                    />
                  </div>
                ))
              ) : (
                <div className="text-center text-[14px] nunito-semibold text-[#919191] py-4">
                  You're all caught up! Notifications will appear here as soon
                  as there is something new to review.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
